import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Users, Euro, UserPlus } from 'lucide-react';
import NewCustomerModal from '../components/superadmin/NewCustomerModal';

const NAVY = '#1B3A6B';
const STARTER = '#2B7FD4';
const PRO = '#6D28D9';
const POS = '#16A34A';
const NEG = '#DC2626';

export default function SuperadminRevenuePage() {
  const { t, i18n } = useTranslation();
  const [d, setD] = useState(null);
  const [sortKey, setSortKey] = useState('monthly_amount_eur');
  const [sortDir, setSortDir] = useState('desc');
  const [showNewCustomer, setShowNewCustomer] = useState(false);

  const reload = () => api.get('/api/platform/revenue').then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);

  const sortedCustomers = useMemo(() => {
    if (!d) return [];
    const arr = [...d.customers_list];
    arr.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [d, sortKey, sortDir]);

  if (!d) return <div className="p-8 text-text-secondary">{t('common.loading')}</div>;

  // Use the active i18n locale for both number and date formatting so DE
  // shows 1.234,56 € / 15.01.2026 and EN shows 1,234.56 € / 01/15/2026.
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'de-DE';
  const fmtEur = (n) => `€ ${(n || 0).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtEurInt = (n) => `€ ${(n || 0).toLocaleString(locale)}`;
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
  const totalMRR = sortedCustomers.reduce((s, c) => s + (c.monthly_amount_eur || 0), 0);
  const vatPct = Math.round((d.vat_rate || 0.20) * 100);

  function header(key, label) {
    const active = sortKey === key;
    return (
      <th onClick={() => { if (active) setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('asc'); } }} className="cursor-pointer select-none" data-testid={`rev-sort-${key}`}>
        {label} {active ? (sortDir === 'asc' ? '▲' : '▼') : ''}
      </th>
    );
  }

  return (
    <div data-testid="revenue-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('revenue.title')}</h1>
          <p className="mt-1 text-sm text-text-secondary">{t('revenue.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowNewCustomer(true)}
          className="so-btn so-btn-primary"
          data-testid="open-new-customer-btn"
        >
          <UserPlus size={14}/> {t('revenue.newCustomer.openButton')}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6" data-testid="revenue-kpis">
        <Card label={t('revenue.kpi.mrrNetto')} value={fmtEurInt(d.mrr_netto_eur)} color={NAVY} icon={Euro} testid="kpi-mrr-netto"/>
        <Card label={t('revenue.kpi.mrrVat', { pct: vatPct })} value={fmtEur(d.mrr_vat_eur)} color={NAVY} testid="kpi-mrr-vat"/>
        <Card label={t('revenue.kpi.mrrBrutto')} value={fmtEur(d.mrr_brutto_eur)} color={NAVY} icon={Euro} testid="kpi-mrr-brutto"/>
        <Card label={t('revenue.kpi.arrNetto')} value={fmtEurInt(d.arr_netto_eur)} color={NAVY} testid="kpi-arr"/>
        <Card label={t('revenue.kpi.activeCustomers')} value={d.customers} color={NAVY} icon={Users} testid="kpi-customers"/>
        <Card label={t('revenue.kpi.growthMom')} value={`${d.growth_mom_pct >= 0 ? '+' : ''}${d.growth_mom_pct}%`} color={d.growth_mom_pct >= 0 ? POS : NEG} icon={d.growth_mom_pct >= 0 ? TrendingUp : TrendingDown} testid="kpi-growth"/>
      </div>

      <div className="so-card p-4 mt-4 flex items-center justify-between flex-wrap gap-3" data-testid="setup-retainer-card">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-text-secondary font-semibold">{t('revenue.setup.title')}</p>
          <p className="text-sm text-text-secondary mt-0.5">
            {t('revenue.setup.body', { count: d.setup_paid_count || 0, fee: fmtEurInt(d.setup_fee_eur || 399) })}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xl font-bold" style={{ color: POS }} data-testid="setup-retainer-netto">{fmtEurInt(d.setup_revenue_netto_eur || 0)} {t('revenue.netto')}</p>
          <p className="text-xs text-text-secondary">{fmtEur(d.setup_revenue_brutto_eur || 0)} {t('revenue.bruttoInclVat', { pct: vatPct })}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="so-card p-5 lg:col-span-2">
          <h2 className="text-sm font-semibold mb-3">{t('revenue.charts.mrrTrend')}</h2>
          <div style={{ width: '100%', minHeight: 240, height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={d.mrr_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
                <XAxis dataKey="month" tick={{ fontSize: 11 }}/>
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`}/>
                <Tooltip formatter={(v, n) => [`€ ${v}`, n === 'mrr' ? t('revenue.netto') : t('revenue.brutto')]} wrapperStyle={{ fontSize: 12 }}/>
                <Legend wrapperStyle={{ fontSize: 12 }}/>
                <Line type="monotone" dataKey="mrr" stroke={NAVY} strokeWidth={3} dot={{ r: 3 }} name={t('revenue.kpi.mrrNetto')}/>
                <Line type="monotone" dataKey="mrr_brutto" stroke="#94A3B8" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name={t('revenue.kpi.mrrBrutto')}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="so-card p-5">
          <h2 className="text-sm font-semibold mb-3">{t('revenue.charts.customersByPlan')}</h2>
          <div style={{ width: '100%', minHeight: 240, height: 240 }} data-testid="rev-plan-chart">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={d.plan_breakdown} dataKey="count" nameKey="plan" innerRadius={50} outerRadius={85} label>
                  {d.plan_breakdown.map((p, i) => (
                    <Cell key={i} fill={p.plan === 'pro' ? PRO : STARTER}/>
                  ))}
                </Pie>
                <Tooltip wrapperStyle={{ fontSize: 12 }}/>
                <Legend wrapperStyle={{ fontSize: 12 }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6" data-testid="rev-cycle-breakdown">
        {(d.cycle_breakdown || []).map((c) => (
          <div key={c.cycle} className="so-card p-5" data-testid={`rev-cycle-${c.cycle}`}>
            <p className="text-[11px] uppercase tracking-wider text-text-secondary font-semibold">
              {c.cycle === 'annual' ? t('revenue.cycle.annual') : t('revenue.cycle.monthly')}
            </p>
            <p className="mt-2 text-2xl font-bold text-navy">{c.count} <span className="text-sm font-normal text-text-secondary">{t('revenue.customer', { count: c.count })}</span></p>
            <p className="mt-1 text-sm text-text-secondary">MRR: <strong className="text-navy">{fmtEurInt(c.mrr_netto)}</strong> {t('revenue.netto')} · {fmtEur(c.mrr_netto * (1 + (d.vat_rate || 0.20)))} {t('revenue.brutto')}</p>
          </div>
        ))}
      </div>

      <div className="so-card p-5 mt-6">
        <h2 className="text-sm font-semibold mb-3">{t('revenue.charts.newCustomers')}</h2>
        <div style={{ width: '100%', minHeight: 220, height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={d.new_customers_per_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
              <XAxis dataKey="month" tick={{ fontSize: 11 }}/>
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false}/>
              <Tooltip wrapperStyle={{ fontSize: 12 }}/>
              <Bar dataKey="new_customers" fill={POS} name={t('revenue.newCustomers')}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="so-card mt-6 overflow-x-auto" data-testid="rev-table">
        <table className="so-table">
          <thead>
            <tr>
              {header('name', t('revenue.table.name'))}
              {header('plan', t('revenue.table.plan'))}
              {header('billing_cycle', t('revenue.table.billing'))}
              {header('monthly_amount_eur', t('revenue.table.mrrNetto'))}
              <th>{t('revenue.table.mrrBrutto')}</th>
              <th data-testid="rev-col-setup">{t('revenue.setup.column')}</th>
              {header('subscription_started_at', t('revenue.table.since'))}
              {header('contract_end', t('revenue.table.commitmentUntil'))}
              {header('next_payment_at', t('revenue.table.nextPayment'))}
            </tr>
          </thead>
          <tbody>
            {sortedCustomers.map((c) => (
              <tr key={c._id} data-testid={`rev-row-${c.slug}`}>
                <td className="font-medium">{c.name}</td>
                <td>
                  <span className="so-pill" style={{ background: c.plan === 'pro' ? `${PRO}22` : `${STARTER}22`, color: c.plan === 'pro' ? PRO : STARTER }}>
                    {c.plan === 'pro' ? 'Pro' : 'Starter'}
                  </span>
                </td>
                <td>
                  <span className={`so-pill ${c.billing_cycle === 'annual' ? 'so-pill-success' : 'so-pill-neutral'}`} data-testid={`rev-cycle-${c.slug}`}>
                    {c.billing_cycle === 'annual' ? t('revenue.cycle.annualShort') : t('revenue.cycle.monthlyShort')}
                  </span>
                </td>
                <td className="font-mono">{fmtEurInt(c.monthly_amount_eur)}</td>
                <td className="font-mono text-text-secondary">{fmtEur(c.monthly_amount_brutto_eur)}</td>
                <td data-testid={`rev-setup-${c.slug}`}>
                  <span className={`so-pill ${c.setup_fee_requested ? 'so-pill-success' : 'so-pill-neutral'}`}>
                    {c.setup_fee_requested ? t('revenue.setup.yes') : t('revenue.setup.no')}
                  </span>
                </td>
                <td className="text-text-secondary">{fmtDate(c.subscription_started_at)}</td>
                <td className="text-text-secondary" data-testid={`rev-contract-${c.slug}`}>{c.billing_cycle === 'annual' ? fmtDate(c.contract_end) : '—'}</td>
                <td className="text-text-secondary">{fmtDate(c.next_payment_at)}</td>
              </tr>
            ))}
            <tr className="bg-background font-semibold">
              <td colSpan={3}>{t('revenue.totalMrr')}</td>
              <td className="font-mono" data-testid="rev-total-mrr">{fmtEurInt(totalMRR)}</td>
              <td className="font-mono text-text-secondary">{fmtEur(totalMRR * (1 + (d.vat_rate || 0.20)))}</td>
              <td colSpan={4} className="text-text-secondary">{t('revenue.customer', { count: sortedCustomers.length })}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-[11px] text-text-secondary leading-relaxed">{t('revenue.legalNote', { pct: vatPct })}</p>

      {/* Demo accounts — read-only, greyed out, EXCLUDED from all metrics
          above. We display them separately so superadmins can see the demo
          state without it polluting MRR/ARR. */}
      {d.demo_list && d.demo_list.length > 0 && (
        <div className="so-card mt-6 overflow-x-auto opacity-75" data-testid="demo-accounts-section">
          <div className="px-5 py-4 border-b border-border bg-background/60">
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center justify-center rounded-md text-[10px] uppercase tracking-wider font-bold px-2 py-0.5"
                style={{ background: '#94A3B8', color: '#fff' }}
                data-testid="demo-section-badge"
              >
                {t('revenue.demo.badge')}
              </span>
              <h2 className="text-sm font-semibold text-text">{t('revenue.demo.title')}</h2>
              <span className="text-xs text-text-secondary ml-auto" data-testid="demo-section-count">
                {t('revenue.demo.count', { count: d.demo_count || d.demo_list.length })}
              </span>
            </div>
            <p className="mt-1 text-xs text-text-secondary">{t('revenue.demo.subtitle')}</p>
          </div>
          <table className="so-table">
            <thead>
              <tr>
                <th>{t('revenue.table.name')}</th>
                <th>{t('revenue.table.plan')}</th>
                <th>{t('revenue.table.billing')}</th>
                <th>{t('revenue.table.mrrNetto')}</th>
                <th>{t('revenue.setup.column')}</th>
                <th>{t('revenue.table.since')}</th>
              </tr>
            </thead>
            <tbody>
              {d.demo_list.map((c) => (
                <tr key={c._id} data-testid={`demo-row-${c.slug}`}>
                  <td className="font-medium">
                    <span className="inline-flex items-center gap-2">
                      {c.name}
                      <span
                        className="inline-flex items-center justify-center rounded text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5"
                        style={{ background: '#94A3B8', color: '#fff' }}
                        data-testid={`demo-badge-${c.slug}`}
                      >
                        {t('revenue.demo.badge')}
                      </span>
                    </span>
                  </td>
                  <td className="text-text-secondary">{c.plan === 'pro' ? 'Pro' : 'Starter'}</td>
                  <td className="text-text-secondary">{c.billing_cycle === 'annual' ? t('revenue.cycle.annualShort') : t('revenue.cycle.monthlyShort')}</td>
                  <td className="font-mono text-text-secondary">{fmtEurInt(c.monthly_amount_eur)}</td>
                  <td className="text-text-secondary">{c.setup_fee_requested ? t('revenue.setup.yes') : t('revenue.setup.no')}</td>
                  <td className="text-text-secondary">{fmtDate(c.subscription_started_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showNewCustomer && (
        <NewCustomerModal
          onClose={() => setShowNewCustomer(false)}
          onCreated={() => { reload(); }}
        />
      )}
    </div>
  );
}

function Card({ label, value, color, icon: Icon, testid }) {
  return (
    <div className="so-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wide text-text-secondary font-semibold">{label}</p>
        {Icon && <Icon size={14} style={{ color }}/>}
      </div>
      <p className="mt-1 text-xl font-bold tabular-nums" style={{ color }}>{value}</p>
    </div>
  );
}
